import React from 'react'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../../../../state/refresh'
import { ProjectWithComments, ProjectItem } from '../../../../types/project'

export const PROJECT_CATEGORIES = [{ id: 'AGD', label: 'AGD' }, { id: 'ceramic', label: 'Armatura i ceramika' }, { id: 'decorations', label: 'Dekoracje' }, { id: 'other', label: 'Inne' }, { id: 'furniture', label: 'Meble' }, { id: 'tapered furniture', label: 'Meble tapicerowane' }, { id: 'coverings', label: 'Okładziny' }, { id: 'lighting', label: 'Oświetlenie' }, { id: 'lightingLED', label: 'Oświetlenie LED' }, { id: 'tiles', label: 'Płytki' }, { id: 'carving', label: 'Sztukateria' }, { id: 'wallpaper', label: 'Tapety' }, { id: 'services', label: 'Usługi' }]

interface ItemModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: () => void
  mode: 'add' | 'edit'
  itemData: ProjectItem | Omit<ProjectItem, 'id' | 'position'>
  setItemData: (data: ProjectItem | Omit<ProjectItem, 'id' | 'position'>) => void
  project: ProjectWithComments | null
}

// Funkcja do renderowania drzewa kategorii jako <option>
function renderCategoryOptions (tree: Array<{ id: string, label: string, children?: Array<{ id: string, label: string, children?: any[] }> }>, prefix = ''): JSX.Element[] {
  return tree.flatMap(category => [
    <option key={category.id} value={category.id}>
      {prefix}{category.label}
    </option>,
    ...((category.children != null) ? renderCategoryOptions(category.children, prefix + category.label + ' / ') : [])
  ])
}

export const ItemModal: React.FC<ItemModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  itemData,
  setItemData,
  project
}) => {
  const dispatch = useDispatch()
  const isEdit = mode === 'edit'

  if (!isOpen) return null

  return (
    <div
      className='modal-backdrop' style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1050,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}
    >
      <div
        className='modal-content' style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
          width: '100%',
          maxWidth: '1200px',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div className='modal-header border-bottom p-3'>
          <h5 className='modal-title m-0'>{isEdit ? 'Edytuj Element' : 'Dodaj Element'}</h5>
          <button
            type='button'
            className='close'
            onClick={onClose}
            style={{ padding: '0.5rem 1rem' }}
          >
            <span>&times;</span>
          </button>
        </div>
        <div className='modal-body' style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          <form>
            {/* Podstawowe informacje */}
            <div className='form-row mb-4'>
              <div className='col-12 mb-3'>
                <label className='form-label'>Nazwa</label>
                <input
                  type='text'
                  className='form-control'
                  value={itemData.name}
                  onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                />
              </div>
            </div>
            <div className='form-row mb-4'>
              <div className='col-12 col-md-6 mb-3'>
                <label className='form-label'>Typ</label>
                <select
                  className='form-control'
                  value={itemData.type}
                  onChange={(e) => setItemData({ ...itemData, type: e.target.value as 'product' | 'service' | 'note' | 'visualization' | 'attachment' })}
                >
                  <option value='product'>Produkt</option>
                  <option value='service'>Usługa</option>
                  <option value='note'>Notatka</option>
                  <option value='visualization'>Wizualizacja</option>
                  <option value='attachment'>Załącznik</option>
                </select>
              </div>
              <div className='col-12 col-md-6 mb-3'>
                <label className='form-label'>Cena (PLN)</label>
                <input
                  type='number'
                  className='form-control'
                  value={itemData.price}
                  min='0'
                  step='0.01'
                  onChange={(e) => setItemData({ ...itemData, price: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className='form-row mb-4'>
              <div className='col-12 col-md-4 mb-3'>
                <label className='form-label'>Kategoria</label>
                <select
                  className='form-control'
                  value={itemData.category || ''}
                  onChange={e => setItemData({ ...itemData, category: e.target.value })}
                >
                  <option value=''>Wybierz kategorię</option>
                  {renderCategoryOptions(PROJECT_CATEGORIES)}
                </select>
              </div>
              <div className='col-12 col-md-4 mb-3'>
                <label className='form-label'>Nr kat.</label>
                <input
                  type='text'
                  className='form-control'
                  value={itemData.catalogNumber || ''}
                  onChange={e => setItemData({ ...itemData, catalogNumber: e.target.value })}
                  placeholder='Nr kat.'
                />
              </div>
              <div className='col-12 col-md-4 mb-3'>
                <label className='form-label'>Dostawca</label>
                <input
                  type='text'
                  className='form-control'
                  value={itemData.supplier || ''}
                  onChange={e => setItemData({ ...itemData, supplier: e.target.value })}
                  placeholder='Dostawca'
                />
              </div>
            </div>
            {/* Ilość, jednostka i koszty */}
            <div className='form-row mb-4'>
              <div className='col-12 col-md-3 mb-3'>
                <label className='form-label'>Ilość</label>
                <input
                  type='number'
                  className='form-control'
                  value={itemData.quantity}
                  min='1'
                  onChange={(e) => setItemData({ ...itemData, quantity: parseInt(e.target.value) })}
                />
              </div>
              <div className='col-12 col-md-3 mb-3'>
                <label className='form-label'>Jednostka</label>
                <input
                  type='text'
                  className='form-control'
                  value={itemData.unit}
                  onChange={(e) => setItemData({ ...itemData, unit: e.target.value })}
                />
              </div>
              <div className='col-12 col-md-3 mb-3'>
                <label className='form-label'>Koszt dostawy (PLN)</label>
                <input
                  type='number'
                  className='form-control'
                  value={itemData.deliveryCost || ''}
                  min='0'
                  step='0.01'
                  onChange={(e) => setItemData({ ...itemData, deliveryCost: parseFloat(e.target.value) })}
                />
              </div>
              <div className='col-12 col-md-3 mb-3'>
                <label className='form-label'>Rabat (%)</label>
                <input
                  type='number'
                  className='form-control'
                  value={itemData.discountPercentage || ''}
                  min='0'
                  max='100'
                  step='0.01'
                  onChange={(e) => setItemData({ ...itemData, discountPercentage: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className='form-row mb-4'>
              <div className='col-12 col-md-3 mb-3'>
                <label className='form-label'>Marża (%)</label>
                <input
                  type='number'
                  className='form-control'
                  value={itemData.marginPercentage || ''}
                  min='0'
                  step='0.01'
                  onChange={(e) => setItemData({ ...itemData, marginPercentage: parseFloat(e.target.value) })}
                />
              </div>
              <div className='col-12 col-md-3 mb-3'>
                <label className='form-label'>VAT (%)</label>
                <input
                  type='number'
                  className='form-control'
                  value={itemData.vatPercentage || ''}
                  min='0'
                  max='100'
                  step='0.01'
                  onChange={(e) => setItemData({ ...itemData, vatPercentage: parseFloat(e.target.value) })}
                />
              </div>
              <div className='col-12 col-md-6 mb-3'>
                <label className='form-label'>Etap</label>
                <select
                  className='form-control'
                  value={itemData.stageId}
                  onChange={(e) => setItemData({ ...itemData, stageId: e.target.value })}
                >
                  {project?.stages.map(stage => (
                    <option key={stage.id} value={stage.id}>
                      {stage.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* URL i wymiary */}
            <div className='form-row mb-4'>
              <div className='col-12 col-md-6 mb-3'>
                <label className='form-label'>URL</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='https://example.com'
                  value={itemData.url || ''}
                  onChange={(e) => setItemData({ ...itemData, url: e.target.value })}
                />
              </div>
              <div className='col-12 col-md-6 mb-3'>
                <label className='form-label'>Wymiary/Pojemność</label>
                <input
                  type='text'
                  className='form-control'
                  placeholder='np. 100x200 cm, 250 ml'
                  value={itemData.dimensions || ''}
                  onChange={(e) => setItemData({ ...itemData, dimensions: e.target.value })}
                />
              </div>
            </div>
            {/* Opis */}
            <div className='form-row mb-4'>
              <div className='col-12'>
                <label className='form-label'>Opis</label>
                <textarea
                  className='form-control'
                  rows={3}
                  value={itemData.description}
                  onChange={(e) => setItemData({ ...itemData, description: e.target.value })}
                />
              </div>
            </div>
            {/* Notatka */}
            <div className='form-row mb-4'>
              <div className='col-12'>
                <label className='form-label'>Notatka</label>
                <textarea
                  className='form-control'
                  rows={3}
                  value={itemData.note || ''}
                  onChange={(e) => setItemData({ ...itemData, note: e.target.value })}
                  placeholder='Dodaj notatkę do elementu...'
                />
              </div>
            </div>
            {/* Obrazek */}
            <div className='form-row'>
              <div className='col-12 col-md-8 mb-3'>
                <label className='form-label'>URL obrazu</label>
                <input
                  type='text'
                  className='form-control'
                  value={itemData.imageUrl || ''}
                  onChange={(e) => setItemData({ ...itemData, imageUrl: e.target.value })}
                  placeholder='https://example.com/image.jpg'
                />
              </div>
              <div className='col-12 col-md-4 mb-3'>
                {itemData.imageUrl && (
                  <div
                    className='image-preview' style={{
                      height: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                  >
                    <img
                      src={itemData.imageUrl}
                      alt='Podgląd'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                        dispatch(
                          RefreshActions.setToastMessage({
                            message: 'Nie udało się załadować obrazu'
                          })
                        )
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
        <div className='modal-footer border-top p-3'>
          <div className='d-flex justify-content-center w-100 gap-2'>
            <button
              className={`
                  tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                  tw-text-white
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                `}
              onClick={onClose}
            >
              Anuluj
            </button>
            <button
              className={`
                  tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                  tw-text-white
                  tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                `}
              onClick={onSave}
              disabled={!itemData.name?.trim() || !itemData.stageId}
            >
              {isEdit ? 'Zapisz Zmiany' : 'Dodaj Element'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
